import { Entity, isResourceEntity } from '@backstage/catalog-model';
import { Text } from '@backstage/ui';
import { CodeSnippet, Link } from '@backstage/core-components';
import { useEntity, EntityInfoCard } from '@backstage/plugin-catalog-react';
import styles from './AksKubeloginCard.module.css';

export const AZURE_SUBSCRIPTION_ID_ANNOTATION = 'azure.com/subscription-id';
export const AZURE_RESOURCE_GROUP_ANNOTATION = 'azure.com/resource-group';
export const AZURE_CLUSTER_NAME_ANNOTATION = 'azure.com/cluster-name';
export const PRODUCTION_ENVIRONMENT_TAG = 'prd';

const annotationsRequired = [
  AZURE_SUBSCRIPTION_ID_ANNOTATION,
  AZURE_RESOURCE_GROUP_ANNOTATION,
  AZURE_CLUSTER_NAME_ANNOTATION,
];

export const isAksKubeloginCardAvailable = (entity: Entity): boolean => {
  if (!isResourceEntity(entity) || entity.spec?.type !== 'kubernetes-cluster') {
    return false;
  }
  const annotations = entity.metadata.annotations ?? {};
  return annotationsRequired.every(key => key in annotations);
};

export const AksKubeloginCard = () => {
  const { entity } = useEntity();
  const tags = entity.metadata.tags ?? [];

  if (!isAksKubeloginCardAvailable(entity)) return null;

  const annotations = entity.metadata.annotations!;
  const subscriptionId = annotations[AZURE_SUBSCRIPTION_ID_ANNOTATION];
  const resourceGroup = annotations[AZURE_RESOURCE_GROUP_ANNOTATION];
  const clusterName = annotations[AZURE_CLUSTER_NAME_ANNOTATION];

  const productionEnvironment = tags.includes(PRODUCTION_ENVIRONMENT_TAG);

  const subscription = `az account set --subscription ${subscriptionId}`;

  const credentials = `az aks get-credentials --resource-group ${resourceGroup} --name ${clusterName} --overwrite-existing`;

  const authentication = `kubelogin convert-kubeconfig -l azurecli`;

  return (
    <EntityInfoCard title="Connect to AKS Cluster with kubectl">
      <Text variant="title-x-small" as="h6">
        Prerequisites
      </Text>
      <ol className={styles.list}>
        <li className={styles.listItem}>
          <Link
            to="https://learn.microsoft.com/en-us/cli/azure/install-azure-cli"
            externalLinkIcon
            variant="body1"
          >
            Install Azure CLI
          </Link>
        </li>
        <li className={styles.listItem}>
          <Link
            to="https://kubernetes.io/docs/tasks/tools/install-kubectl-windows/"
            externalLinkIcon
            variant="body1"
          >
            Install kubectl
          </Link>
        </li>
        <li className={styles.listItem}>
          <Link
            to="https://azure.github.io/kubelogin/install.html"
            externalLinkIcon
            variant="body1"
          >
            Install kubelogin
          </Link>
        </li>
      </ol>
      <Text variant="title-x-small" as="h6">
        Instructions
      </Text>
      <Text variant="body-medium" style={{ paddingBottom: '1rem' }} as="p">
        Run the following commands to connect to the cluster using kubectl
      </Text>
      <Text variant="body-medium">Set the cluster subscription</Text>
      <CodeSnippet
        text={subscription}
        language="bash"
        showCopyCodeButton
        customStyle={{ padding: '1rem' }}
      />
      <Text variant="body-medium">Download cluster credentials</Text>
      <CodeSnippet
        text={credentials}
        language="bash"
        showCopyCodeButton
        customStyle={{ padding: '1rem' }}
      />
      <Text variant="body-medium">Use kubelogin plugin for authentication</Text>
      <CodeSnippet
        text={authentication}
        language="bash"
        showCopyCodeButton
        customStyle={{ padding: '1rem' }}
      />
      {productionEnvironment && (
        <Text variant="body-small" color="secondary">
          Note: Make sure to use your admin account for all production
          environments
        </Text>
      )}
    </EntityInfoCard>
  );
};
